import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Checkbox, Group, Paper, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';

import { BasicButton } from '@components/Button';
import {
  CollectionCard,
  ManagementLayout,
  PageTitle,
  PaginatedList,
  SearchInput,
} from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { toErrorMessage } from '@util/toErrorMessage';

function flagSummary(exercise) {
  const bits = [];
  if (exercise.uses_weight) bits.push('weight');
  if (exercise.uses_reps) bits.push('reps');
  if (exercise.uses_duration) bits.push('duration');
  if (exercise.uses_distance) bits.push('distance');
  return bits.length ? bits.join(' · ') : 'no prescription fields';
}

function ExerciseCreateForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [usesWeight, setUsesWeight] = useState(true);
  const [usesReps, setUsesReps] = useState(true);
  const [usesDuration, setUsesDuration] = useState(false);
  const [usesDistance, setUsesDistance] = useState(false);
  const [saving, setSaving] = useState(false);

  function onSubmit(event) {
    event.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    axios
      .post('/exercises/', {
        name: name.trim(),
        description: description.trim() || null,
        uses_weight: usesWeight,
        uses_reps: usesReps,
        uses_duration: usesDuration,
        uses_distance: usesDistance,
      })
      .then(({ data }) => {
        const exercise = data.response?.[0];
        toast.success('Exercise created');
        onCreated(exercise);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  return (
    <Paper p="md" withBorder radius="md" component="form" onSubmit={onSubmit}>
      <Title order={4} mb="sm">
        New exercise
      </Title>
      <Stack gap="sm">
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={2}
          autosize
        />
        <Text size="sm" c="dimmed">
          Add coaching cues and other tags on the exercise detail page after
          create.
        </Text>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Prescription fields
          </Text>
          <Checkbox
            label="Uses weight"
            checked={usesWeight}
            onChange={(e) => setUsesWeight(e.currentTarget.checked)}
          />
          <Checkbox
            label="Uses reps"
            checked={usesReps}
            onChange={(e) => setUsesReps(e.currentTarget.checked)}
          />
          <Checkbox
            label="Uses duration"
            checked={usesDuration}
            onChange={(e) => setUsesDuration(e.currentTarget.checked)}
          />
          <Checkbox
            label="Uses distance"
            checked={usesDistance}
            onChange={(e) => setUsesDistance(e.currentTarget.checked)}
          />
        </Stack>
        <Group>
          <BasicButton
            text="Create exercise"
            type="submit"
            loading={saving}
            disabled={saving || !name.trim()}
            color="var(--brand-brick)"
            variant="filled"
            style={{
              background: 'var(--brand-brick)',
              borderColor: 'var(--brand-brick)',
            }}
            primary
          />
          <BasicButton
            text="Cancel"
            type="button"
            onClick={onCancel}
            disabled={saving}
            color="gray"
            variant="outline"
          />
        </Group>
      </Stack>
    </Paper>
  );
}

export default function AdminExercises() {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [listKey, setListKey] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  function onCreated(exercise) {
    setCreating(false);
    setListKey((key) => key + 1);
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-exercises-picker'] });
    if (exercise?.id) {
      navigate(`/exercises/${exercise.id}`);
    }
  }

  return (
    <div>
      <PageTitle
        title="Exercises"
        subtitle="Library flags control what can be prescribed on workouts"
        icon={entityIcon.exercise()}
        iconColor={entityColor.exercise}
        actions={
          !creating ? (
            <BasicButton
              text="Add exercise"
              onClick={() => setCreating(true)}
              color={entityColor.exercise}
              variant="light"
              leftSection={entityIcon.exercise()}
            />
          ) : null
        }
      />
      <ManagementLayout
        createComponent={
          creating ? (
            <ExerciseCreateForm
              onCreated={onCreated}
              onCancel={() => setCreating(false)}
            />
          ) : null
        }
        dataComponent={
          <div>
            <SearchInput
              value={name}
              onChange={setName}
              placeholder="Search by name..."
            />
            <PaginatedList
              key={listKey}
              requestInfo={{
                url: '/retrieve-exercises/',
                data: { name },
              }}
              displayFormatter={(item) => {
                const exercise = item.original;
                return (
                  <CollectionCard
                    key={exercise.id}
                    to={`/exercises/${exercise.id}`}
                    title={exercise.name}
                    meta={
                      <Text size="sm" c="dimmed">
                        {flagSummary(exercise)}
                      </Text>
                    }
                    badge={exercise.status_formatted}
                    badgeColor={
                      exercise.status_formatted === 'active'
                        ? 'teal'
                        : exercise.status_formatted === 'inactive'
                          ? 'gray'
                          : 'red'
                    }
                  />
                );
              }}
            />
          </div>
        }
      />
    </div>
  );
}
